import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { IMAGES } from "@/lib/site";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const GALLERY = [
    { src: IMAGES.himalaya, alt: "Sunset over Himalayan snow peaks", cat: "Himalayas" },
    { src: IMAGES.rajasthanDoorway, alt: "Ornate palace doorway in Rajasthan", cat: "Rajasthan" },
    { src: IMAGES.udaipurGolden, alt: "City Palace on Lake Pichola at golden hour, Udaipur", cat: "Rajasthan" },
    { src: IMAGES.kerala, alt: "Houseboat gliding through the Kerala backwaters", cat: "Kerala" },
    { src: IMAGES.varanasiGhats, alt: "Boats on the Ganges at the ghats of Varanasi", cat: "Spiritual" },
    { src: IMAGES.goa, alt: "Palm-fringed Goan beach at sunset", cat: "Goa" },
    { src: IMAGES.rajasthanPalace, alt: "Grand Rajasthani palace architecture", cat: "Rajasthan" },
    { src: IMAGES.varanasiDawn, alt: "Dawn at the riverfront ghats of Varanasi", cat: "Spiritual" },
    { src: IMAGES.hero, alt: "Golden light on Himalayan ridgelines", cat: "Himalayas" },
    { src: IMAGES.udaipurPalace, alt: "Lake palace of Udaipur from the water", cat: "Rajasthan" },
];

const CATEGORIES = ["All", "Himalayas", "Rajasthan", "Kerala", "Goa", "Spiritual"];

const Gallery = () => {
    const [cat, setCat] = useState("All");
    const [active, setActive] = useState(null);
    const filtered = cat === "All" ? GALLERY : GALLERY.filter((g) => g.cat === cat);

    return (
        <>
            <PageHeader
                eyebrow="Gallery"
                title="Postcards from India"
                description="A glimpse of the landscapes, palaces and rivers our journeys are woven from."
            />
            <section className="py-20 lg:py-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap justify-center gap-3" data-testid="gallery-filters">
                        {CATEGORIES.map((c) => (
                            <button
                                key={c}
                                onClick={() => setCat(c)}
                                data-testid={`gallery-filter-${c.toLowerCase()}`}
                                className={`rounded-full border px-5 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-all duration-300 ${
                                    cat === c
                                        ? "border-brass bg-brass text-navy"
                                        : "border-navy/20 text-navy hover:border-brass hover:text-brass-dark"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>

                    <motion.div layout className="mt-14 columns-1 gap-5 sm:columns-2 lg:columns-3" data-testid="gallery-grid">
                        <AnimatePresence mode="popLayout">
                            {filtered.map((img) => (
                                <motion.figure
                                    layout
                                    key={img.src}
                                    initial={{ opacity: 0, scale: 0.96 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.96 }}
                                    transition={{ duration: 0.4 }}
                                    className="group relative mb-5 break-inside-avoid overflow-hidden rounded-xl cursor-zoom-in"
                                    onClick={() => setActive(img)}
                                    data-testid="gallery-item"
                                >
                                    <img src={img.src} alt={img.alt} className="w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                                    <figcaption className="absolute bottom-0 left-0 right-0 translate-y-3 p-5 text-left text-sm font-medium text-ivory opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                                        {img.alt}
                                    </figcaption>
                                </motion.figure>
                            ))}
                        </AnimatePresence>
                    </motion.div>
                </div>
            </section>

            <Dialog open={!!active} onOpenChange={(open) => !open && setActive(null)}>
                <DialogContent className="max-w-4xl border-brass/30 bg-navy p-2 sm:p-3" data-testid="gallery-lightbox">
                    <DialogTitle className="px-4 py-3 text-sm font-medium text-ivory/80">{active?.alt}</DialogTitle>
                    {active ? (
                        <img src={active.src} alt={active.alt} className="max-h-[75vh] w-full rounded-lg object-contain" />
                    ) : null}
                    <button
                        onClick={() => setActive(null)}
                        aria-label="Close image view"
                        data-testid="gallery-lightbox-close"
                        className="absolute right-3 top-3 rounded-full bg-white/10 p-2 text-ivory transition-colors hover:bg-brass hover:text-navy"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default Gallery;
